import { uniqueId } from 'lodash'
import { PropsWithChildren, FC, useState, useContext } from 'react'
import { useLocation } from 'react-router-dom'
import { Divider, notification } from 'antd'
import { DownloadOutlined, PauseCircleOutlined } from '@ant-design/icons'
import { useEnablePhase2AutoOtUI } from '@OpsWb/logic'
import { ProgressDataType } from '@OpsWb/components/JobStatusProgressBar/types'
import AutoExceptionButton from '@ProdWb/JobDetails/AutoExceptionActionButton'
import ExceptionButtonStyles from '@ProdWb/JobDetails/AutoExceptionActionButton/styles.css'
import styles from '@ProdWb/JobDetails/styles.css'
import { FETCH_FAILURE_RECORDS_LIMIT } from '@ProdWb/components/ExceptionFailuresDrawer/constants'

import { ExceptionFailuresSideDrawer } from '../../components/ExceptionFailuresDrawer'
import Box from '~/core/Box'
import Button from '~/core/Button'
import Text from '~/core/Text'
import { JobRecordType } from '../../JobList/types'
import { ExceptionFailureModal } from '../../components/ExceptionFailureModal'
import { JOB_STATUS } from '../constants'
import Link from '~/core/Link'
import {
    OPS_PRODUCTION_PATH,
    OPS_QUALITY_PATH,
} from '~/redux-modules/preferences'
import { JobDetailsContext } from '../JobDetailsContext'

import { InspectionQualityDocumentsDownload } from './InspectionQualityDocumentsDownload'

type CollapsableHeaderSectionPropsType = {
    jobName: string
    jobId: number
    jobStatus: string
    pauseException: boolean | undefined
    exceptionCount: number
    isCurrentMilestoneOnTrack: boolean
    currentData?: ProgressDataType
    jobDataForCreateException?: JobRecordType
    isDownloadInspectionDocsBtnEnabled?: boolean
    jobRequestId?: number
}

const CollapsableHeaderSection: FC<
    PropsWithChildren<CollapsableHeaderSectionPropsType>
> = ({
    jobName,
    jobId,
    pauseException,
    exceptionCount,
    isCurrentMilestoneOnTrack,
    currentData,
    jobStatus,
    jobDataForCreateException,
    isDownloadInspectionDocsBtnEnabled,
    jobRequestId,
}) => {
    const location = useLocation()
    const isQualityPage = location.pathname.includes('quality')
    const isJobDetailsPage = location.pathname.includes('job-details')
    const data = useContext(JobDetailsContext)

    const [showExceptionFailureDrawer, setShowExceptionFailureDrawer] =
        useState(false)
    const [jobData, setJobData] = useState<JobRecordType | null>(null)
    const handleCancel = () => {
        setJobData(null)
    }
    const isAutoOtPhase2Flag = useEnablePhase2AutoOtUI()

    async function getInspectionQualityDocuments(event: Event) {
        event.stopPropagation()
        const notificationQueueId = uniqueId(
            'download-inspection-quality-documents',
        )

        return await notification.open({
            key: notificationQueueId,
            duration: null,
            description: (
                <InspectionQualityDocumentsDownload
                    jobRequestId={jobRequestId || 0}
                    notificationKey={notificationQueueId}
                />
            ),
            message: 'Download files',
        })
    }

    return (
        <Box
            className={styles.collapseableHeader}
            flex
            justifyContent="between"
        >
            <Box direction="col">
                <Text className={styles.jobNameHeaderTitle}>{jobName}</Text>
                {isQualityPage && (
                    <Link
                        onClick={(event: Event) => event?.stopPropagation()}
                        href={`${OPS_PRODUCTION_PATH}/job-details/${jobId}`}
                        target="_blank"
                    >
                        View job details
                    </Link>
                )}
                {isJobDetailsPage &&
                    (jobStatus === JOB_STATUS.ACCEPTED ||
                        jobStatus === JOB_STATUS.COMPLETED) && (
                        <Link
                            onClick={(event: Event) => event?.stopPropagation()}
                            href={`${OPS_QUALITY_PATH}/${data.jobRequestId}`}
                            target="_blank"
                        >
                            View inspection
                        </Link>
                    )}
            </Box>
            <Box display="flex" justifyContent="center" alignItems="center">
                {isQualityPage && isDownloadInspectionDocsBtnEnabled && (
                    <Button
                        className={ExceptionButtonStyles.headerBtn}
                        onClick={getInspectionQualityDocuments}
                        name="download-inspection-report"
                        placement="download-inspection-files"
                        data-test-target="download-inspection-files"
                    >
                        <DownloadOutlined />
                        <Text
                            className={styles.createResumeExceptionButtonText}
                        >
                            Download quality documents
                        </Text>
                    </Button>
                )}
                {isAutoOtPhase2Flag &&
                    (jobStatus === JOB_STATUS.ACCEPTED ||
                        jobStatus === JOB_STATUS.UNACCEPTED) && (
                        <AutoExceptionButton
                            jobId={Number(jobId)}
                            pauseException={pauseException}
                        />
                    )}
                <Button
                    onClick={(evt: Event) => {
                        evt.stopPropagation()
                        setJobData(jobDataForCreateException as JobRecordType)
                    }}
                    className={ExceptionButtonStyles.headerBtn}
                >
                    <Text className={styles.createResumeExceptionButtonText}>
                        Create Exception
                    </Text>
                </Button>
                {exceptionCount > 0 && (
                    <span
                        onClick={event => {
                            event.stopPropagation()
                            setShowExceptionFailureDrawer(true)
                        }}
                        className={styles.countStyle}
                        data-test-target="job-exception-count"
                    >
                        {exceptionCount}
                    </span>
                )}
                {jobData && (
                    <ExceptionFailureModal
                        handleCancel={handleCancel}
                        jobData={jobData}
                        refetchExceptionsVariables={{
                            filters: { jobId: jobData.id },
                            limit: FETCH_FAILURE_RECORDS_LIMIT,
                        }}
                    />
                )}
                {showExceptionFailureDrawer && (
                    <ExceptionFailuresSideDrawer
                        drawerData={{
                            jobData: jobDataForCreateException as JobRecordType,
                            showJobExceptions: true,
                            showJobRequestExceptions: true,
                        }}
                        onClose={() => {
                            setShowExceptionFailureDrawer(false)
                        }}
                    />
                )}
                <Divider
                    className={styles.jobHeaderDividerStyle}
                    type="vertical"
                />
                {isAutoOtPhase2Flag && pauseException && (
                    <span
                        onClick={event => event.stopPropagation()}
                        className={styles.pausedException}
                    >
                        <PauseCircleOutlined
                            style={{ padding: '2px 4px 4px 2px' }}
                        />
                        Automated exceptions paused
                    </span>
                )}
                {currentData && (
                    <span
                        onClick={event => event.stopPropagation()}
                        className={
                            isCurrentMilestoneOnTrack
                                ? styles.qcLabelComplete
                                : styles.qcLabelPending
                        }
                    >
                        {currentData.milestoneTitle}
                    </span>
                )}
            </Box>
        </Box>
    )
}

export default CollapsableHeaderSection
